import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
} from "@angular/core";
import { DropdownItemComponent } from "./dropdown-item.component";
import { Dropdown } from "./dropdown.service";

@Component({
  selector: "icn-dropdown",
  templateUrl: "./dropdown.component.html",
  styleUrls: ["./dropdown.component.scss"],
  providers: [Dropdown],
  host: {
    "[class.top]": "position === 'top'",
    "[class.bottom]": "position === 'bottom'",
    "[class.short]": "!!short",
    "[class.medium]": "!!medium",
    "[class.large]": "!!large",
    "[class.multiple-size]": "allowMultiple || autoHeight",
  },
})
export class DropdownComponent implements AfterContentInit, OnChanges {
  constructor(private dropdownService: Dropdown) {}

  @Input()
  textAlign: string = "center";
  @Input()
  showSettingsIcon: boolean = false;
  @Output()
  settingsIconClicked = new EventEmitter();

  @Input()
  returnItems: boolean = false;

  @Input() placeholder: string;

  @Input()
  showPlaceholderWhenEmpty = false;

  @Input()
  selectedValue: any;

  @Input()
  selectedValues: any[] = [];

  @Input()
  listOpen = false;

  @Input()
  showSelection = true;

  @Input()
  position = "top";

  @Input()
  allowMultiple: boolean = false;

  @Input()
  autoHeight: boolean = false;

  @Input()
  short = false;

  @Input()
  medium = false;

  @Input()
  large = false;

  @Input()
  allowDefault = false;

  @Input()
  defaultValue = undefined;

  @Input()
  equalFunc: (valueA, valueB) => boolean = null;

  @Input()
  disabled: boolean = false;

  @Output()
  listOpenChange = new EventEmitter<boolean>();

  @Output()
  selectedValueChange = new EventEmitter();

  @Output()
  selectedItemChange = new EventEmitter();

  @Output()
  selectedValuesChange = new EventEmitter();

  @Input()
  selectedClass: string = "";

  /* list of options will drop down and not up if this is set to true */
  @Input()
  listDown = false;

  @Output()
  itemClicked = new EventEmitter();

  @Output()
  defaultValueChange = new EventEmitter();

  selectedItem: DropdownItemComponent;

  selectedItems: DropdownItemComponent[] = [];

  @Input()
  selectedContainerClass: string = "";

  selectAllTitle = "Select All";

  @ContentChildren(DropdownItemComponent)
  items: QueryList<DropdownItemComponent>;
  selectAllSelected: boolean;

  toggleListOpen() {
    this.listOpen = !this.listOpen;
    this.listOpenChange.emit(this.listOpen);
  }

  onSettingsIconClicked(e: Event, item?: unknown): void {
    this.settingsIconClicked.emit(!item ? e : { event: e, item: item });
    e.stopImmediatePropagation();
  }
  onClickOutside() {
    if (this.listOpen) {
      this.listOpen = false;
      this.listOpenChange.emit(false);
    }
  }

  setDefaultByFirstSelected() {
    if (this.selectedItems.length === 0) {
      this.defaultValue = undefined;
    } else {
      this.defaultValue = this.selectedItems[0].value;
    }
    this.defaultValueChange.emit(this.defaultValue);
    this.items.forEach((item) => {
      item.isDefault = item.value === this.defaultValue;
    });
  }

  setDefault(item: DropdownItemComponent) {
    this.defaultValue = item.value;
    this.defaultValueChange.emit(item.value);
    this.items.forEach((item) => {
      item.isDefault = item.value === this.defaultValue;
    });
  }

  onItemClicked(item: DropdownItemComponent) {
    if (!this.allowMultiple) {
      this.items.forEach((i) => {
        i.selected = this.areEqual(i.value, item.value);
      });
      this.selectedValue = item.value;

      this.selectedValueChange.emit(item.value); // dispach output event

      if (item.item) this.selectedItemChange.emit(item.item);

      this.selectedItem = item;

      this.listOpen = false;
      this.listOpenChange.emit(false);
    } else {
      item.selected = !item.selected;

      if (!item.selected) {
        let valueIndex = this.selectedValues.findIndex((v) =>
          this.areEqual(v, item.value),
        );
        if (valueIndex > -1) {
          this.selectedValues.splice(valueIndex, 1);
        }

        let itemIndex = this.selectedItems.indexOf(item);
        if (itemIndex > -1) {
          this.selectedItems.splice(itemIndex, 1);
        }
      } else {
        if (
          this.selectedValues.findIndex((x) =>
            this.areEqual(x, item.value),
          ) === -1
        ) {
          this.selectedValues.push(item.value);
        }
        if (this.selectedItems.findIndex((x) => x === item) == -1) {
          this.selectedItems.push(item);
        }
        if (this.allowDefault && this.selectedValues.length === 1) {
          item.isDefault = true;
          this.defaultValue = item.value;
          this.defaultValueChange.emit(item.value);
        }
      }

      if (this.returnItems) {
        this.selectedValuesChange.emit(
          new DropdownSelection(this.selectedItems, this.selectedValues),
        );
      } else {
        this.selectedValuesChange.emit(this.selectedValues);
      }

      //this.setSelectAllTitleAccordingToStatus();
    }

    this.itemClicked.emit({
      value: item.value,
      isSelected: item.selected,
      selectedValue: this.selectedValue,
      multipleSelectedValues: this.selectedValues,
    });
  }

  showNothing() {
    if (
      this.showPlaceholderWhenEmpty &&
      this.placeholder &&
      !this.allowMultiple &&
      !this.selectedItem
    ) {
      return false;
    }
    return (
      (this.selectedValue != 0 &&
        !this.selectedValue &&
        this.selectedValues.length === 0) ||
      !this.showSelection
    );
  }

  updateItems() {
    let self = this;
    if (!this.allowMultiple) {
      this.items.forEach((item) => {
        item.selected = this.areEqual(item.value, self.selectedValue);
        if (item.selected) {
          self.selectedItem = item;
        }
      });
    } else {
      this.selectedItems = [];
      this.items.forEach((item) => {
        item.multiple = true;
        item.selected = self.selectedValues.some((v) =>
          self.areEqual(v, item.value),
        );
        item.isDefault = this.areEqual(this.defaultValue, item.value);
        item.isShowDefaultStatus = this.allowDefault;
        if (item.selected) {
          self.selectedItems.push(item);
        }
      });
    }
    this.setSelectAllTitleAccordingToStatus();
  }

  ngAfterContentInit() {
    this.updateItems();
    this.dropdownService.itemClicked.subscribe(this.onItemClicked.bind(this));
    this.dropdownService.setAsDefault.subscribe(this.setDefault.bind(this));
    this.dropdownService.defaultUnchecked.subscribe(
      this.setDefaultByFirstSelected.bind(this),
    );

    this.selectedValuesChange.subscribe(() =>
      this.setSelectAllTitleAccordingToStatus(),
    );
    this.setSelectAllTitleAccordingToStatus();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.items) {
      return;
    }
    if (this.allowMultiple) {
      if (!!changes["selectedValues"]) {
        this.updateItems();
      }
    } else {
      if (!!changes["selectedValue"]) {
        this.updateItems();
      }
    }
    this.setSelectAllTitleAccordingToStatus();
  }

  isPlaceholderPresented() {
    return (
      (this.allowMultiple &&
        (!this.selectedItems || this.selectedItems.length === 0)) ||
      (!this.allowMultiple && !this.selectedItem)
    );
  }

  formatMultipleItemsTitle() {
    return this.selectedItems.map((item) => item.title).join(", ");
  }

  areEqual(valueA: any, valueB: any) {
    if (!this.equalFunc) {
      return valueA === valueB;
    }

    return this.equalFunc(valueA, valueB);
  }

  onSelectAllClicked(): void {
    if (this.selectAllSelected) {
      //unselect all items
      this.selectedValues.splice(0, this.selectedValues.length);
    } else {
      //select all items
      this.items.forEach((item) => {
        if (
          !this.selectedValues.some((v) => this.areEqual(v, item.value))
        ) {
          this.selectedValues.push(item.value);
        }
      });
    }
    this.updateItems();
    this.setSelectAllTitleAccordingToStatus();
    if (this.returnItems) {
      this.selectedValuesChange.emit(
        new DropdownSelection(this.selectedItems, this.selectedValues),
      );
    } else {
      this.selectedValuesChange.emit(this.selectedValues);
    }
  }
  setSelectAllTitleAccordingToStatus() {
    if (
      this.selectedValues.length === this.items.length &&
      this.items.length > 0
    ) {
      this.selectAllTitle = "UnSelect All";
      this.selectAllSelected = true;
    } else {
      this.selectAllTitle = "Select All";
      this.selectAllSelected = false;
    }
  }
}

export class DropdownSelection {
  constructor(
    public items: any[],
    public values: any[],
  ) {}
}
